import { Location } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  neoPatIcon,
  sideBarStudentIcon,
  sideBarDriveIcon,
  sideBarEligibilityIcon,
  sideBarEmailTemplateIcon,
  sideBarProgrammeMappingIcon,
  sideBarSettingsIcon,
  sideBarPattern,
  republishQueueIcon,
  drivePlannerIcon,
  reminderIcon,
  customMessageIcon,
  metricsIcon,
  chatIcon,
  dashboardIcon,
  reportsIcon,
  defaultProfile
}
  from '@assets';
import { GlobalService } from '../../services/global.service';
import { RemoteConfigService } from '../../services/remote-config.service';

interface MenuGroup {
  title: string;
  items: any[];
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css']
})
export class SidebarComponent implements OnInit, OnDestroy {
  iconsList: any[] = [];
  menuGroups: MenuGroup[] = [];
  path: string = '';
  neoPatIcon: any = neoPatIcon;
  sideBarPattern = sideBarPattern;
  assets_bucket: string = '';
  subscription: any;
  isExpanded = false;
  isHovered = false;
  mainGroups: any[] = [];
  settingsGroup: any[] = [];
  hoveredGroup: string | null = null;
  hoverMenuActive = false;
  hoverMenuTimeout: any;
  floatMenuTop = 0;
  hoveredSettings = false;
  settingsMenuTop = 0;
  userData: any;
  userProfilePicture: string = defaultProfile;
  announcementText: string = '';
  showAnnouncementModal = false;
  topAnnouncements: any[] = [];
  sidebarAnnouncements: any[] = [];
  hoveredAnnouncements = false;
  announcementsMenuTop = 0;
  isItemsLoaded = false;
  hasRecentAnnouncements = false;

  constructor(private router: Router, private location: Location, private globalService: GlobalService, private remoteConfigService: RemoteConfigService) {
    this.formatData();
    const path = this.location.path().split('/')[1]
    this.navClick({ label: path })
    this.assets_bucket = this.globalService.bucketName;
    const group_account_id = JSON.parse(localStorage.getItem('account_details') ?? '{}').group_account_id;
    if (group_account_id) {
      this.neoPatIcon = `https://storage.cloud.google.com/${this.globalService.publicBucket}/logo/${group_account_id}?${new Date().getTime()}`
    }
    else {
      this.neoPatIcon = neoPatIcon;
    }

    // Initialize user data and profile picture
    this.userData = JSON.parse(localStorage.getItem('user_details') ?? '{}');
    this.initializeProfilePicture();

    this.subscription = this.globalService.imageUpdate.subscribe((data) => {
      if (!data) return;
      this.neoPatIcon = data;
    })

    // Listen for profile updates from header component
    window.addEventListener('profileUpdated', (event: any) => {
      this.refreshProfilePicture();
    });

    // Initialize static groups immediately
    this.initializeStaticGroups();
  }

  formatData() {
    this.globalService.driveLabelSubject.subscribe((label) => {
      let path = this.router.url;
      const {
        drive_label,
      } = label.labeling;

      this.iconsList = [
        { label: `planner`, value: drivePlannerIcon, toolTip: `${drive_label} Planner`, isActive: path.includes('planner'), isBeta: false },
        { label: `${drive_label.toLowerCase()}`, value: sideBarDriveIcon, toolTip: `${drive_label}`, isActive: path.includes('drives') || path.includes('jobs'), isBeta: false },
        { label: 'republish-queue', value: republishQueueIcon, toolTip: 'Republish Queue', isActive: path.includes('republish-queue'), isBeta: false },
        { label: 'reminders', value: reminderIcon, toolTip: `${drive_label} Reminder`, isActive: path.includes('reminders'), isBeta: false },
        { label: 'students', value: sideBarStudentIcon, toolTip: 'Students', isActive: path.includes('students'), isBeta: false },
        { label: 'eligibility', value: sideBarEligibilityIcon, toolTip: 'Eligibility Check', isActive: path.includes('eligibility'), isBeta: false },
        { label: 'whatsapp', value: chatIcon, toolTip: 'Whatsapp', isActive: path.includes('whatsapp'), isBeta: true },
        { label: 'email-template', value: sideBarEmailTemplateIcon, toolTip: 'Email Templates', isActive: path.includes('email-template'), isBeta: false },
        { label: 'email-insights', value: customMessageIcon, toolTip: 'Email Insights', isActive: path.includes('email-insights'), isBeta: true },
        { label: 'reports', value: reportsIcon, toolTip: 'Reports', isActive: path.includes('reports'), isBeta: true },
        { label: 'engagement-metrics', value: metricsIcon, toolTip: 'Engagement Metrics', isActive: path.includes('engagement-metrics'), isBeta: false },
        { label: 'programme-mapping', value: sideBarProgrammeMappingIcon, toolTip: 'Programme Mapping', isActive: path.includes('programme-mapping'), isBeta: false },
        { label: 'settings', value: sideBarSettingsIcon, toolTip: 'Settings', isActive: path.includes('settings'), isBeta: false }
      ];

      this.groupMenuItems();
    })
  }

  groupMenuItems() {
    this.menuGroups = [
      {
        title: 'Drive Management',
        items: this.iconsList.filter(item =>
          ['planner', 'drives', 'jobs', 'reminders', 'republish-queue'].includes(item.label)
        )
      },
      {
        title: 'Student Management',
        items: this.iconsList.filter(item =>
          ['students', 'eligibility'].includes(item.label)
        )
      },
      {
        title: 'Communication',
        items: this.iconsList.filter(item =>
          ['whatsapp', 'email-template', 'email-insights'].includes(item.label)
        )
      },
      {
        title: 'Analytics & Reports',
        items: this.iconsList.filter(item =>
          ['engagement-metrics', 'reports'].includes(item.label)
        )
      },
      {
        title: 'Configuration',
        items: this.iconsList.filter(item =>
          ['programme-mapping', 'settings'].includes(item.label)
        )
      }
    ];
  }

  private initializeStaticGroups() {
    // Initialize settings group immediately (static)
    this.settingsGroup = [
      {
        label: 'settings', icon: sideBarSettingsIcon, popout: [
          { label: 'Programme Mapping', value: sideBarProgrammeMappingIcon, action: 'programme-mapping' },
          { label: 'Portal Settings', value: sideBarSettingsIcon, action: 'portal-settings' }
        ]
      }
    ];

    // Initialize main groups with static structure (items will be populated when iconsList is ready)
    this.mainGroups = [
      {
        label: 'Drive Management',
        icon: drivePlannerIcon,
        groupKey: 'drive',
        items: []
      },
      {
        label: 'Student Management',
        icon: sideBarStudentIcon,
        groupKey: 'student',
        items: []
      },
      {
        label: 'Communication',
        icon: sideBarEmailTemplateIcon,
        groupKey: 'communication',
        items: []
      },
      {
        label: 'Analytics & Reports',
        icon: metricsIcon,
        groupKey: 'analytics',
        items: []
      }
    ];
  }

  private updateMainGroups() {
    // Update main groups with actual items once iconsList is populated
    this.mainGroups = [
      {
        label: 'Drive Management',
        icon: drivePlannerIcon,
        groupKey: 'drive',
        items: this.iconsList.filter(item => ['planner', 'drives', 'jobs', 'republish-queue', 'reminders',].includes(item.label))
      },
      {
        label: 'Student Management',
        icon: sideBarStudentIcon,
        groupKey: 'student',
        items: this.iconsList.filter(item => ['students', 'eligibility'].includes(item.label))
      },
      {
        label: 'Communication',
        icon: sideBarEmailTemplateIcon,
        groupKey: 'communication',
        items: this.iconsList.filter(item => ['whatsapp', 'email-template', 'email-insights',].includes(item.label))
      },
      {
        label: 'Analytics & Reports',
        icon: metricsIcon,
        groupKey: 'analytics',
        items: this.iconsList.filter(item => ['reports', 'engagement-metrics'].includes(item.label))
      }
    ];
    
    // Mark items as loaded
    this.isItemsLoaded = true;
  }

  async ngOnInit() {
    this.globalService.sidebarExpandedObservable.subscribe((expanded: boolean) => {
      this.isExpanded = expanded;
      this.isHovered = expanded;
    });

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        const pathname = this.router.url.split('/')[1];
        this.navClick({ label: pathname });
      }
    });

    let res: any = await this.globalService.getCollegeProfile();
    if (res.success) {
      this.neoPatIcon = res.data?.logo ? res.data?.logo : neoPatIcon;
    }

    await this.initializeProfilePicture();
    await this.fetchSidebarAnnouncements();

    // Update main groups with actual items once iconsList is populated
    this.updateMainGroups();
  }

  async initializeProfilePicture() {
    if (this.userData?.profile_picture) {
      try {
        const signedUrl = await this.globalService.getSignedURL({
          bucketName: this.globalService.publicBucket,
          fileName: this.userData.profile_picture
        });
        this.userProfilePicture = signedUrl.data.response;
      } catch (error) {
        console.error('Error loading profile picture:', error);
        this.userProfilePicture = defaultProfile;
      }
    } else {
      this.userProfilePicture = defaultProfile;
    }

    // Update profile group with the actual profile picture
    // This logic is no longer needed as profile is in header
  }

  async refreshProfilePicture() {
    // Update user data from localStorage
    this.userData = JSON.parse(localStorage.getItem('user_details') ?? '{}');
    await this.initializeProfilePicture();
  }

  onSidebarHover() {
    if (!this.isExpanded) {
      this.isHovered = true;
      this.expandSidebar();
    }
  }

  onSidebarLeave(event: Event) {
    this.isHovered = false;
    this.isExpanded = false;
  }

  expandSidebar() {
    this.isExpanded = true;
    this.isHovered = true;

  }

  // onCloseButtonClick(event: Event) {
  //   event.stopPropagation();
  //   event.preventDefault();
  //   this.closeSidebar();
  // }

  navClick(option: any) {
    this.iconsList.forEach((icon) => icon.isActive = false);
  
    const selectedIcon = this.iconsList.find((icon) => icon.label === option.label);
    if (selectedIcon) {
      selectedIcon.isActive = true;
    }
  
    if (Object.keys(option).length > 1) {
      this.router.navigateByUrl(option.label);
    }
  }

  imageLoadError(event: any) {
    // Check if this is a profile picture error
    if (event.target.style.borderRadius === '50%') {
      event.target.src = defaultProfile;
    } else {
    event.target.src = neoPatIcon;
    }
  }

  onGroupHover(groupKey: string, btn?: HTMLElement) {
    clearTimeout(this.hoverMenuTimeout);
    // Clear other menu states
    this.hoveredSettings = false;
    this.hoveredGroup = groupKey;
    this.hoverMenuActive = true;
    if (btn) {
      this.floatMenuTop = this.calculateMenuPosition(btn, this.mainGroups.find(g => g.groupKey === groupKey)?.items?.length || 0);
    }
  }

  private calculateMenuPosition(btn: HTMLElement, itemCount: number): number {
    const rect = btn.getBoundingClientRect();
    const menuHeight = 48 + (itemCount * 48); // 48px header + 48px per item
    let top = rect.top;

    if (top + menuHeight > window.innerHeight) {
      top = window.innerHeight - menuHeight - 8; // 8px margin from bottom
      if (top < 0) top = 8; // 8px margin from top
    }

    return top;
  }

  onGroupLeave() {
    this.hoverMenuTimeout = setTimeout(() => {
      this.hoveredGroup = null;
      this.hoverMenuActive = false;
    }, 120); // short delay to allow moving between icon and menu
  }

  onFloatMenuEnter() {
    clearTimeout(this.hoverMenuTimeout);
    this.hoverMenuActive = true;
  }

  onFloatMenuLeave() {
    this.hoverMenuTimeout = setTimeout(() => {
      this.hoveredGroup = null;
      this.hoverMenuActive = false;
    }, 120);
  }

  onSettingsHover(btn?: HTMLElement) {
    clearTimeout(this.hoverMenuTimeout);
    this.hoveredSettings = true;
    this.hoveredAnnouncements = false; // Close announcements if open
    if (btn) {
      this.settingsMenuTop = this.calculateMenuPosition(btn, 3);
    }
  }

  onSettingsLeave() {
    this.hoverMenuTimeout = setTimeout(() => {
      this.hoveredSettings = false;
    }, 120);
  }

  onSettingsMenuEnter() {
    clearTimeout(this.hoverMenuTimeout);
    this.hoveredSettings = true;
  }

  onSettingsMenuLeave() {
    this.hoverMenuTimeout = setTimeout(() => {
      this.hoveredSettings = false;
    }, 120);
  }

  async handleSettingsAction(action: string) {
    if (action === 'programme-mapping') {
      this.router.navigateByUrl('programme-mapping');
    } else if (action === 'portal-settings') {
      this.router.navigateByUrl('settings');
    } else if (action === 'announcements') {
      this.announcementText = await this.remoteConfigService.getAnnouncements();

      try {
        const announcements = JSON.parse(this.announcementText);
        this.topAnnouncements = announcements
          .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 2);
      } catch (error) {
        console.error('Error parsing announcements:', error);
        this.topAnnouncements = [];
      }

      this.showAnnouncementModal = true;
    }
  }

  goToRoute(route: string) {
    this.router.navigateByUrl(route);
    this.closeAnnouncementModal();
  }

  closeAnnouncementModal() {
    this.showAnnouncementModal = false;
  }

  async fetchSidebarAnnouncements() {
    try {
      const announcementText = await this.remoteConfigService.getAnnouncements();
      const announcements = JSON.parse(announcementText);
      this.sidebarAnnouncements = announcements
        .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 2);
      
      // Check for recent announcements (within past 2 days)
      this.checkForRecentAnnouncements(announcements);
    } catch (error) {
      this.sidebarAnnouncements = [];
      this.hasRecentAnnouncements = false;
    }
  }

  private checkForRecentAnnouncements(announcements: any[]) {
    if (!announcements || announcements.length === 0) {
      this.hasRecentAnnouncements = false;
      return;
    }

    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    // Check if any announcement is within the past 2 days
    this.hasRecentAnnouncements = announcements.some((announcement: any) => {
      const announcementDate = new Date(announcement.date);
      return announcementDate >= twoDaysAgo;
    });
  }

  onAnnouncementsHover(btn?: HTMLElement) {
    clearTimeout(this.hoverMenuTimeout);
    this.hoveredAnnouncements = true;
    this.hoveredSettings = false;
    if (btn) {
      // Calculate accurate height for announcement banners
      const headerHeight = 48; // Title height
      const bannerHeight = 80; // Each banner is ~80px (padding + content + margin)
      const totalHeight = headerHeight + (this.sidebarAnnouncements.length * bannerHeight);
      const itemCount = totalHeight / 48; // Convert to equivalent item count for existing calculation
      this.announcementsMenuTop = this.calculateMenuPosition(btn, itemCount);
    }
  }

  onAnnouncementsLeave() {
    this.hoverMenuTimeout = setTimeout(() => {
      this.hoveredAnnouncements = false;
    }, 120);
  }

  onAnnouncementsMenuEnter() {
    clearTimeout(this.hoverMenuTimeout);
    this.hoveredAnnouncements = true;
  }

  onAnnouncementsMenuLeave() {
    this.hoverMenuTimeout = setTimeout(() => {
      this.hoveredAnnouncements = false;
    }, 120);
  }

  goToAnnouncement(route: string) {
    if (route) this.router.navigateByUrl(route);
    this.hoveredAnnouncements = false;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
    this.subscription.unsubscribe();
    }
    window.removeEventListener('profileUpdated', this.refreshProfilePicture.bind(this));
  }
}

